import { Button } from "@sellar-io/components";
import { useState, useMemo } from "react";
import {
  InputLabel,
  InputContainer,
  InputOption,
  InputSelect,
} from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useVolumeDiscountsList } from "../../../hooks/api/v2/volumeDiscounts/useVolumeDiscountsList";
import { usePriceListVolumeDiscountsCreate } from "../../../hooks/api/v2/priceListVolumeDiscounts/usePriceListVolumeDiscountsCreate";
import { SpinnerLegacy } from "../../../components";
import { capitalize } from "@sellar-io/common";
import { AllFulfillmentTypesType } from "@sellar-io/api-v2/types";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useVolumeDiscountMode } from "../../../hooks/useVolumeDiscountMode";
import { ConditionalVolumeDiscountsPrompt } from "./VolumeDiscountModeDialogs";

type FulfillmentType = Extract<
  AllFulfillmentTypesType,
  "direct" | "courier" | "haulier" | "collection" | "sellarDistribution"
> | null;

export const VolumeDiscountGroupAssignDialog = ({
  active,
  setActive,
  priceListId,
  existingAssignments,
  onSetUpConditionalVolumeDiscounts,
}: {
  active: boolean;
  setActive: (active: boolean) => void;
  priceListId: number;
  existingAssignments: Array<FulfillmentType>;
  onSetUpConditionalVolumeDiscounts: () => void;
}) => {
  const [error, setError] = useState("");
  const [selectedVolumeDiscountId, setSelectedVolumeDiscountId] = useState<
    number | null
  >(null);
  const [selectedFulfillmentTypes, setSelectedFulfillmentTypes] = useState<
    Set<NonNullable<FulfillmentType>>
  >(new Set());
  const [isDefault, setIsDefault] = useState(false);

  const volumeDiscountsListQuery = useVolumeDiscountsList(
    {
      sortBy: "name",
      sortDir: "asc",
      limit: 100,
    },
    { enabled: active },
  );

  const volumeDiscounts = useMemo(
    () => volumeDiscountsListQuery.data?.pages.flat() ?? [],
    [volumeDiscountsListQuery.data],
  );

  const assignMutation = usePriceListVolumeDiscountsCreate(priceListId);
  const volumeDiscountMode = useVolumeDiscountMode();
  const isBasicMode = volumeDiscountMode.isBasicMode;

  const sellarDistributionEnabled = useRemoteFeatureFlags().isActive(
    "sellarDistributionBeta",
  );

  const fulfillmentTypes: Array<{
    value: NonNullable<FulfillmentType>;
    label: string;
  }> = [
    ...(sellarDistributionEnabled
      ? [{ value: "sellarDistribution" as const, label: "Sellar Distribution" }]
      : []),
    { value: "direct", label: "Direct" },
    { value: "courier", label: "Courier" },
    { value: "haulier", label: "Haulier" },
    { value: "collection", label: "Collection" },
  ];

  const existingFulfillmentTypes = new Set(existingAssignments);

  const availableSpecificTypes = fulfillmentTypes.filter(
    (ft) => !existingFulfillmentTypes.has(ft.value),
  );

  const defaultAlreadyExists = existingAssignments.includes(null);
  const [previousActive, setPreviousActive] = useState(false);
  if (active !== previousActive) {
    setPreviousActive(active);
    if (active) {
      setIsDefault(isBasicMode || existingAssignments.length === 0);
      setSelectedFulfillmentTypes(new Set());
    }
  }

  const toggleFulfillmentType = (value: NonNullable<FulfillmentType>) => {
    setSelectedFulfillmentTypes((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const onSubmit = async () => {
    setError("");

    if (!selectedVolumeDiscountId) {
      setError("Please select a volume discount group");
      return;
    }

    if (!isBasicMode && !isDefault && selectedFulfillmentTypes.size === 0) {
      setError("Please select at least one delivery method");
      return;
    }

    try {
      if (isBasicMode || isDefault) {
        await assignMutation.mutateAsync({
          VolumeDiscountsId: selectedVolumeDiscountId,
          fulfillmentType: null,
        });
      } else {
        await Promise.all(
          [...selectedFulfillmentTypes].map((type) =>
            assignMutation.mutateAsync({
              VolumeDiscountsId: selectedVolumeDiscountId!,
              fulfillmentType: type,
            }),
          ),
        );
      }
      setActive(false);
      setSelectedVolumeDiscountId(null);
      setSelectedFulfillmentTypes(new Set());
      setIsDefault(false);
    } catch (err) {
      const message =
        apiV2AssertError(err)?.response?.data.message ??
        "Failed to assign volume discount group";
      setError(message);
    }
  };

  const disabled =
    !selectedVolumeDiscountId ||
    (!isBasicMode && !isDefault && selectedFulfillmentTypes.size === 0);

  return (
    <Dialog active={active} onDismiss={() => setActive(false)}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={() => setActive(false)}
        title="Add a set of volume discounts"
        description={
          isBasicMode
            ? "You can apply one volume discount set to this price list. It will apply to every delivery method."
            : "You can apply your volume discount sets to more than one price list, and set under which delivery conditions they apply each time"
        }
      />
      <DialogContent className="flex flex-col gap-4">
        {volumeDiscountsListQuery.isLoading ? (
          <SpinnerLegacy />
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <InputLabel>Select volume discount set</InputLabel>
              <InputContainer>
                <InputSelect
                  value={selectedVolumeDiscountId?.toString() ?? ""}
                  onChange={(e) =>
                    setSelectedVolumeDiscountId(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                >
                  <InputOption value="">Select a group...</InputOption>
                  {volumeDiscounts.map((vd) => (
                    <InputOption key={vd.id} value={vd.id.toString()}>
                      {vd.name}
                    </InputOption>
                  ))}
                </InputSelect>
              </InputContainer>
            </div>

            {!isBasicMode && (
              <div className="flex flex-col gap-2">
                <InputLabel>Select delivery conditions</InputLabel>
                <p className="text-zinc-500">
                  You can choose to only have this volume discount set apply
                  when the order is being fulfilled by certain delivery methods.
                  Select which delivery methods below
                </p>
                <button
                  type="button"
                  onClick={() =>
                    !defaultAlreadyExists && setIsDefault((v) => !v)
                  }
                  className={`flex w-full items-center gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${
                    defaultAlreadyExists
                      ? "cursor-not-allowed border-zinc-100 bg-zinc-50 text-zinc-400"
                      : isDefault
                        ? "border-zinc-400 bg-zinc-200 text-zinc-900"
                        : "border-zinc-200 text-zinc-700 hover:border-zinc-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isDefault}
                    disabled={defaultAlreadyExists}
                    readOnly
                    className="h-4 w-4 rounded border-zinc-300 accent-zinc-600"
                  />
                  Default (all delivery methods)
                  {defaultAlreadyExists && (
                    <span className="ml-auto text-xs">Already assigned</span>
                  )}
                </button>
                {availableSpecificTypes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {availableSpecificTypes.map((ft) => {
                      const isSelected = selectedFulfillmentTypes.has(ft.value);
                      return (
                        <button
                          key={ft.value}
                          type="button"
                          onClick={() =>
                            !isDefault && toggleFulfillmentType(ft.value)
                          }
                          className={`flex w-[calc(50%-4px)] items-center gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${
                            isDefault
                              ? "cursor-not-allowed border-zinc-100 bg-zinc-50 text-zinc-400"
                              : isSelected
                                ? "border-zinc-400 bg-zinc-200 text-zinc-900"
                                : "border-zinc-200 text-zinc-700 hover:border-zinc-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isDefault}
                            onChange={() => toggleFulfillmentType(ft.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 rounded border-zinc-300 accent-zinc-600"
                          />
                          {capitalize(ft.label)}
                        </button>
                      );
                    })}
                  </div>
                )}
                {availableSpecificTypes.length === 0 &&
                  defaultAlreadyExists && (
                    <p className="text-sm text-zinc-500">
                      All delivery method slots are already assigned
                    </p>
                  )}
              </div>
            )}
            {isBasicMode && (
              <ConditionalVolumeDiscountsPrompt
                onClick={() => {
                  setActive(false);
                  onSetUpConditionalVolumeDiscounts();
                }}
              />
            )}
          </>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </DialogContent>
      <DialogCTA>
        <Button
          onClick={onSubmit}
          disabled={disabled}
          loading={assignMutation.isPending}
        >
          Add
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
